import { observable } from 'mobx'
import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { WorkspaceBranding } from '@/features/iam/workspaces/workspace-branding'
import { WorkspaceConfig } from '@/features/iam/workspaces/workspace-config'
import { once } from 'lodash-es'

export type WorkspaceArchivalStatus = 'ACTIVE' | 'ARCHIVING' | 'ARCHIVED'

/**
 * Workspace classification.
 */
export type WorkspaceClassification =
  'B2C' | 'B2B' | 'B2B2C' | 'B2E' | 'DEMO' | 'FIRM' | 'TXF'

/**
 * A domain the workspace is served from.
 */
export interface WorkspaceDomain {
  type: 'Slug' | 'Custom'
  domain: string
}

/**
 * A custom field on the members of a workspace.
 */
export interface MemberMetadataField {
  id: string
  type: 'TEXT' | 'NUMERIC' | 'BOOL'
  title: string
  description?: string
  active: boolean
  permissions: ('ADMIN_WRITE' | 'USER_READ' | 'USER_WRITE')[]
}

export type WorkspaceProps = ModelProps<Workspace>

export class Workspace extends Model<Workspace> {
  @observable accessor id = Id.none
  @observable accessor name = ''
  @observable accessor description = ''
  @observable accessor slug = ''
  @observable accessor archivalStatus: WorkspaceArchivalStatus = 'ACTIVE'
  @observable accessor domains: WorkspaceDomain[] = []
  @observable accessor config = WorkspaceConfig.default
  @observable accessor memberMetadataFields: MemberMetadataField[] = []
  @observable accessor branding = WorkspaceBranding.default
  @observable accessor classification: WorkspaceClassification = 'B2B'

  /**
   * Empty workspace instance..
   */
  static get none() {
    return lazyNone()
  }
}

const lazyNone = once(() =>
  new Workspace().set({
    name: 'Worklayer',
    description: 'Worklayer Work Portal',
  }),
)
